import React, { useEffect, useRef, useState } from 'react';
import DraggableCard from './DraggableCard';
import AddMilestoneCard from '../AddMilestoneCard';
import useOverflowDetector from '../../shared/useOverflowDetector';

export default function DropTarget({
  milestones,
  handleMilestoneClick,
  isOver,
  viewMode,
  shouldScroll,
  handleDeleteMilestone,
  handleSubtaskClick,
  openSubtasks,
  setOpenSubtasks,
  members,
  projectBoardId,
  statusId,
  statusName,
  isProjectRelatedBoard,
  priorityOptions,
  statusOptions,
  handleCloneMilestone,
  defaultLabels,
  reminderOptions,
  handleArchiveMilestone,
  isHovered,
  milestoneStatusOptions,
  isTemplateActive,
  setHasScroll = () => {}
}) {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef(null);
  const scrollRef = useRef(null);
  const isFirstRender = useRef(true);
  const hasInitialData = useRef(false);
  const [showDetailsBox, setDetailsBox] = useState(false);
  const [currentLength, setCurrentLength] = useState(0);
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const hasOverflow = useOverflowDetector(scrollRef);
  const toggleSubtasks = (id) => {
    setOpenSubtasks((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const scrollToBottom = () => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current.scrollTop = scrollRef.current?.scrollHeight;
      }, 100);
    }
  };

  const handleScroll = () => {
    // ✅ show scrollbar while scrolling
    setIsScrolling(true);

    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800);
  };
  useEffect(() => {
    if (!isHovered) {
      setDetailsBox(false);
    }
  }, [isHovered]);

  useEffect(() => {
    setHasScroll(hasOverflow);
  }, [hasOverflow]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !hasOverflow) {
      setScrollbarWidth(0);
      return;
    }

    const measureScrollbar = () => {
      setScrollbarWidth(scrollElement.offsetWidth - scrollElement.clientWidth);
    };

    measureScrollbar();

    const resizeObserver = window.ResizeObserver ? new ResizeObserver(measureScrollbar) : null;
    resizeObserver?.observe(scrollElement);
    window.addEventListener('resize', measureScrollbar);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureScrollbar);
    };
  }, [hasOverflow]);

  // Scroll only when a milestone is added
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Still receiving the initial async data load — don't scroll
    if (!hasInitialData.current) {
      hasInitialData.current = true;
      setCurrentLength(milestones?.length ?? 0);
      return;
    }

    if (milestones?.length > 0 && milestones.length > currentLength) {
      setCurrentLength(milestones?.length);
      scrollToBottom();
    }
  }, [milestones.length]);
  return (
    <>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={`${hasOverflow ? 'has-scroll' : ''} drop-scroll-container ${isScrolling ? 'scroll-active' : ''}`}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          // Keep a 10px visual gutter on both overlay-scrollbar macOS
          // and space-reserving Windows scrollbars.
          paddingRight: hasOverflow ? `${Math.max(10 - scrollbarWidth, 0)}px` : undefined,
          maxHeight: `calc(100vh - ${showDetailsBox || isAddingMilestone ? '365px' : '275px'})`,
          ...(isOver ? { minHeight: '80px', marginTop: '10px' } : {})
        }}>
        {milestones.map((milestone, index) => (
          <DraggableCard
            index={index}
            milestone={milestone}
            key={milestone._id}
            handleMilestoneClick={() => handleMilestoneClick(milestone)}
            viewMode={viewMode}
            handleDeleteMilestone={handleDeleteMilestone}
            handleSubtaskClick={handleSubtaskClick}
            showSubtasks={openSubtasks[milestone._id]}
            toggleSubtasks={() => toggleSubtasks(milestone._id)}
            members={members}
            statusOptions={statusOptions}
            isProjectRelatedBoard={isProjectRelatedBoard}
            handleCloneMilestone={handleCloneMilestone}
            defaultLabels={defaultLabels}
            handleArchiveMilestone={handleArchiveMilestone}
            projectBoardId={projectBoardId}
            reminderOptions={reminderOptions}
            milestoneStatusOptions={milestoneStatusOptions}
            isTemplateActive={isTemplateActive}
          />
        ))}
      </div>
      {!viewMode && statusId && (
         <div style={{ display: isHovered || isAddingMilestone ? 'block' : 'none' }}> 
          <AddMilestoneCard
            projectBoardId={projectBoardId}
            statusId={statusId}
            statusName={statusName}
            isProjectRelatedBoard={isProjectRelatedBoard}
            members={members}
            priorityOptions={priorityOptions}
            setDetailsBox={setDetailsBox}
            scrollToBottom={() => scrollToBottom()}
            reminderOptions={reminderOptions}
            statusOptions={statusOptions}
            setIsAddingMilestone={setIsAddingMilestone}
          />
        </div>
      )}
    </>
  );
}
